/**
 * supabaseDocuments.ts — CRUD-Schicht für `case_documents` (Multi-Dokument-Upload).
 *
 * Workflow siehe Migration 0016:
 *   - Mandant ODER Berater lädt Dokumente in `case-documents`-Bucket hoch.
 *   - Pfad-Konvention: cases/{case_id}/{doc_type}/{ts}-{sanitized_filename}
 *   - Versionierung über replaced_by (System-Trigger): bei doc_type ≠
 *     'sonstiges' ersetzt ein neuer Upload den vorherigen.
 *   - Soft-Delete via deleted_at (DELETE auf Tabellenebene nur Admin).
 *
 * Wichtig zur Abgrenzung gegenüber dem alten BWA-Upload (Sprint 2):
 *   - Der alte BWA-Upload nutzt Bucket `bwa-uploads` mit Pfad `{case_id}/{filename}`
 *     und persistiert den Pfad direkt in financial_dataset.raw_bwa_storage_path.
 *   - Diese neue Schicht nutzt Bucket `case-documents` mit Pfad-Prefix `cases/`
 *     und persistiert eine eigene Tabellenzeile pro Dokument.
 *   - Beide laufen parallel; der BWA-Parser (BWA-XLSX → financial_dataset)
 *     greift weiterhin auf den alten Pfad zu, bis er auf die neue Tabelle
 *     migriert wird (Phase A.2).
 *
 * Sicherheits-Garantien (RLS + Trigger 0016):
 *   - SELECT/INSERT/UPDATE/DELETE über can_access_case() — Mandant + Berater
 *     gleichermaßen.
 *   - uploaded_by_role wird DB-seitig aus public.users.role gesetzt — die
 *     UI kann nicht lügen.
 *   - replaced_by wird ausschließlich vom System-Trigger gesetzt.
 *   - Felder bis auf description + deleted_at sind nach INSERT immutable.
 */

import { getSupabaseClient } from './supabase';
import type { CaseDocumentRow, Database, DocumentType } from './supabaseTypes';
import {
  buildCaseDocumentPath,
  isValidDocumentType,
  validateUploadFile,
} from './supabaseDocumentValidation';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const CASE_DOCUMENTS_BUCKET = 'case-documents';

export type { DocumentType, DocumentValidationError } from './supabaseDocumentValidation';
export { buildCaseDocumentPath, sanitizeFilename } from './supabaseDocumentValidation';
export type CaseDocument = CaseDocumentRow;

/**
 * Benutzerfreundliche Labels + Beschreibungen pro doc_type.
 * Wird in UI-Picker, Hover-Tooltip und Empty-State genutzt.
 *
 * Reihenfolge der Keys ist intentional die UI-Sortierreihenfolge
 * (BWA zuerst, sonstiges am Ende).
 */
export const DOCUMENT_TYPES: Readonly<
  Record<DocumentType, { label: string; description: string }>
> = Object.freeze({
  bwa: {
    label: 'BWA',
    description: 'Betriebswirtschaftliche Auswertung der letzten 12 Monate (DATEV-Excel oder PDF).',
  },
  eks: {
    label: 'Anlage EKS',
    description:
      'Vom Jobcenter ausgefüllte oder Eigenausfüllung der Anlage zur Einkommensermittlung.',
  },
  mietvertrag: {
    label: 'Mietvertrag / KdU-Nachweis',
    description: 'Aktueller Mietvertrag oder Bewilligungsbescheid für Kosten der Unterkunft.',
  },
  steuernummer_nachweis: {
    label: 'Steuernummer-Nachweis',
    description: 'Steuerliche Anmeldung beim Finanzamt oder letzter Steuerbescheid.',
  },
  sonstiges: {
    label: 'Sonstige Unterlagen',
    description: 'Auftragsbestand, Liquiditätsplan, weitere relevante Dokumente.',
  },
} satisfies Record<DocumentType, { label: string; description: string }>);

export interface UploadCaseDocumentInput {
  readonly caseId: string;
  readonly docType: DocumentType;
  readonly file: File;
  readonly description?: string;
}

/**
 * Lädt ein Dokument in den `case-documents`-Bucket hoch und erzeugt
 * den Tabellen-Eintrag. Bei Fehlern auf einer der beiden Stufen wird
 * ein konsistenter Fehler geworfen — bei Tabellen-INSERT-Fehlschlag
 * versuchen wir, das gerade hochgeladene Storage-Objekt wieder zu
 * entfernen (best-effort, nur Berater/Admin via RLS).
 */
export async function uploadCaseDocument(input: UploadCaseDocumentInput): Promise<CaseDocument> {
  if (!UUID_RE.test(input.caseId)) {
    throw new Error('uploadCaseDocument: caseId ist kein UUID-Format');
  }
  if (!isValidDocumentType(input.docType)) {
    throw new Error(`uploadCaseDocument: ungültiger docType "${String(input.docType)}"`);
  }
  const validation = validateUploadFile(input.file);
  if (!validation.ok) {
    throw new Error(`uploadCaseDocument: ${validation.error}`);
  }

  const sb = getSupabaseClient();
  const { data: userData, error: userErr } = await sb.auth.getUser();
  if (userErr) {
    throw new Error(`uploadCaseDocument: auth.getUser failed — ${userErr.message}`);
  }
  const uid = userData.user?.id;
  if (typeof uid !== 'string') {
    throw new Error('uploadCaseDocument: kein authentifizierter User');
  }

  const storagePath = buildCaseDocumentPath(input.caseId, input.docType, input.file.name);

  // 1. Storage-Upload
  const uploadOpts: { cacheControl: string; upsert: boolean; contentType?: string } = {
    cacheControl: '3600',
    upsert: false,
    ...(input.file.type ? { contentType: input.file.type } : {}),
  };
  const { error: uploadErr } = await sb.storage
    .from(CASE_DOCUMENTS_BUCKET)
    .upload(storagePath, input.file, uploadOpts);
  if (uploadErr) {
    throw new Error(`uploadCaseDocument [storage]: ${uploadErr.message}`);
  }

  // 2. Tabellen-INSERT — uploaded_by_role lassen wir bewusst weg (Trigger setzt).
  // mime_type-Fallback: Browser liefern manchmal '' für unbekannte Typen;
  // wir nutzen dann 'application/octet-stream' (allowed im case-documents-Bucket
  // ist das zwar nicht — aber der Upload davor wäre dann schon gescheitert).
  const insertPayload: Database['public']['Tables']['case_documents']['Insert'] = {
    case_id: input.caseId,
    doc_type: input.docType,
    storage_path: storagePath,
    original_filename: input.file.name,
    mime_type: input.file.type || 'application/octet-stream',
    size_bytes: input.file.size,
    uploaded_by: uid,
    ...(input.description !== undefined ? { description: input.description } : {}),
  };

  const { data, error } = await sb
    .from('case_documents')
    .insert(insertPayload as never)
    .select('*')
    .single();

  if (error) {
    // Best-effort cleanup — RLS lässt Mandanten-DELETE zwar nicht zu
    // (case_documents_storage_delete: berater_or_admin), aber der Berater-
    // Pfad würde sein eigenes Upload aufräumen können. Mandant müsste
    // den Berater bitten oder der Retention-Cron räumt das später ab.
    void sb.storage.from(CASE_DOCUMENTS_BUCKET).remove([storagePath]);
    throw new Error(`uploadCaseDocument [insert ${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

export interface ListCaseDocumentsOptions {
  /** Wenn true (default): nur replaced_by IS NULL UND deleted_at IS NULL. */
  readonly onlyActive?: boolean;
}

/**
 * Listet alle Dokumente eines Case (RLS filtert automatisch auf Berechtigte).
 * Default: nur aktive Dokumente (= aktuelle Version, nicht soft-deleted).
 * Mit onlyActive=false werden auch ersetzte und soft-deletete Versionen geliefert
 * — sinnvoll für Audit-Ansichten im Berater-Cockpit.
 */
export async function listCaseDocuments(
  caseId: string,
  opts: ListCaseDocumentsOptions = { onlyActive: true },
): Promise<CaseDocument[]> {
  if (!UUID_RE.test(caseId)) return [];
  const onlyActive = opts.onlyActive ?? true;

  const sb = getSupabaseClient();
  let q = sb.from('case_documents').select('*').eq('case_id', caseId);
  if (onlyActive) {
    q = q.is('replaced_by', null).is('deleted_at', null);
  }
  const { data, error } = await q.order('uploaded_at', { ascending: false });

  if (error) {
    throw new Error(`listCaseDocuments [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

/**
 * Soft-Delete eines Dokuments. RLS+Trigger lassen nur den Uploader oder
 * Berater/Admin durch. Das Storage-Objekt bleibt erhalten (Audit-Spur);
 * Hard-Cleanup erfolgt durch Retention-Cron.
 */
export async function deleteCaseDocument(documentId: string): Promise<void> {
  if (!UUID_RE.test(documentId)) {
    throw new Error('deleteCaseDocument: documentId ist kein UUID-Format');
  }

  const sb = getSupabaseClient();
  const update: Database['public']['Tables']['case_documents']['Update'] = {
    deleted_at: new Date().toISOString(),
  };
  const { error } = await sb
    .from('case_documents')
    .update(update as never)
    .eq('id', documentId)
    .is('deleted_at', null);

  if (error) {
    throw new Error(`deleteCaseDocument [${error.code ?? '??'}]: ${error.message}`);
  }
}

/**
 * Phase B (BWA-Konsolidierung):
 * Verknüpft den `storage_path` eines BWA-Dokuments aus `case_documents` mit
 * `financial_dataset.raw_bwa_storage_path`, damit der Score-Engine-Pfad
 * (BWA-Parser → Kennzahlen) auf dasselbe Storage-Objekt zugreifen kann.
 *
 * Aufgerufen direkt nach erfolgreichem `uploadCaseDocument({ docType: 'bwa', … })`.
 *
 * Wichtig: Das Storage-Objekt liegt im `case-documents`-Bucket, nicht im
 * alten `bwa-uploads`-Bucket. Der Score-Engine-Reader muss daher beide
 * Buckets kennen (siehe BWA-Parser in scoreInput.ts) — alternativ: in einer
 * Folge-Migration den BWA-Parser umstellen, dass er den Bucket aus dem
 * Storage-Pfad-Präfix ableitet.
 *
 * Defensive Properties:
 *   - existing-Check + UPDATE oder INSERT (kein upsert, weil unique-index
 *     partial ist und wir period_start/period_end NICHT überschreiben wollen)
 *   - Fehler werden geworfen — der Aufrufer kann entscheiden, ob er das
 *     case_document-Insert wieder zurückrollt (Komplikation: das ist nicht
 *     trivial, weil der user als mandant kein DELETE auf case_documents hat).
 *     Pragma: wir loggen den Fehler, lassen aber den case_documents-Eintrag
 *     stehen — der Berater sieht die BWA in beiden Sichten und kann notfalls
 *     selbst den financial_dataset-Eintrag pflegen.
 */
export async function linkBwaToFinancialDataset(
  caseId: string,
  storagePath: string,
): Promise<void> {
  if (!UUID_RE.test(caseId)) {
    throw new Error('linkBwaToFinancialDataset: caseId ist kein UUID-Format');
  }
  if (typeof storagePath !== 'string' || storagePath.length === 0) {
    throw new Error('linkBwaToFinancialDataset: storagePath fehlt');
  }

  const sb = getSupabaseClient();

  // 1. Existenz-Check
  const { data: existingRaw, error: selectErr } = await sb
    .from('financial_dataset')
    .select('id')
    .eq('case_id', caseId)
    .is('deleted_at', null)
    .maybeSingle();

  if (selectErr) {
    throw new Error(
      `linkBwaToFinancialDataset [select ${selectErr.code ?? '??'}]: ${selectErr.message}`,
    );
  }
  const existing = existingRaw as { id: string } | null;

  // 2a. Update bestehender Zeile
  if (existing?.id) {
    const { error: updateErr } = await sb
      .from('financial_dataset')
      .update({ raw_bwa_storage_path: storagePath } as never)
      .eq('id', existing.id);
    if (updateErr) {
      throw new Error(
        `linkBwaToFinancialDataset [update ${updateErr.code ?? '??'}]: ${updateErr.message}`,
      );
    }
    return;
  }

  // 2b. Neuer Datensatz — period_start / period_end pragmatisch auf YTD
  const today = new Date();
  const periodStart = `${today.getFullYear().toString()}-01-01`;
  const periodEnd = today.toISOString().slice(0, 10);

  const insertPayload = {
    case_id: caseId,
    period_start: periodStart,
    period_end: periodEnd,
    raw_bwa_storage_path: storagePath,
  };
  const { error: insertErr } = await sb.from('financial_dataset').insert(insertPayload as never);
  if (insertErr) {
    throw new Error(
      `linkBwaToFinancialDataset [insert ${insertErr.code ?? '??'}]: ${insertErr.message}`,
    );
  }
}

/**
 * Erzeugt eine signierte Download-URL für das Storage-Objekt eines Dokuments.
 * Default-TTL: 5 Minuten — kurz genug, dass sie nicht in Bookmarks landet,
 * lang genug für einen Browser-Download.
 *
 * Variante 1: documentId → wir laden den storage_path über die Tabelle.
 * RLS sichert dabei, dass nur Berechtigte den Pfad sehen.
 */
export async function getDocumentDownloadUrl(
  documentId: string,
  expiresInSec: number = 300,
): Promise<string> {
  if (!UUID_RE.test(documentId)) {
    throw new Error('getDocumentDownloadUrl: documentId ist kein UUID-Format');
  }
  if (!Number.isFinite(expiresInSec) || expiresInSec <= 0 || expiresInSec > 60 * 60) {
    throw new Error('getDocumentDownloadUrl: expiresInSec muss in (0, 3600] liegen');
  }

  const sb = getSupabaseClient();
  const { data: row, error: selErr } = await sb
    .from('case_documents')
    .select('storage_path')
    .eq('id', documentId)
    .maybeSingle();

  if (selErr) {
    throw new Error(`getDocumentDownloadUrl [select ${selErr.code ?? '??'}]: ${selErr.message}`);
  }
  const path = (row as { storage_path?: string } | null)?.storage_path;
  if (typeof path !== 'string' || path.length === 0) {
    throw new Error('getDocumentDownloadUrl: Dokument nicht gefunden oder kein Zugriff');
  }

  const { data: signed, error: signErr } = await sb.storage
    .from(CASE_DOCUMENTS_BUCKET)
    .createSignedUrl(path, expiresInSec);

  if (signErr) {
    throw new Error(`getDocumentDownloadUrl [sign]: ${signErr.message}`);
  }
  if (typeof signed?.signedUrl !== 'string') {
    throw new Error('getDocumentDownloadUrl: signedUrl unerwartet leer');
  }
  return signed.signedUrl;
}
